'use client'

import {
    PDF_EXTRACT_ALMOST_DONE,
    PDF_EXTRACT_FILLING,
    PDF_EXTRACT_SLOW_HINT,
    PDF_EXTRACT_SLOW_HINT_MS,
    PDF_EXTRACT_STEPS,
    pdfExtractRemainingLabel,
} from '@/app/constants/loanPdfExtract'

interface Props {
    fileName: string
    fileSize: number
    stepIndex: number
    elapsedMs: number
    progressRatio: number
    remainingSeconds: number
    isOvertime: boolean
    onReplace: () => void
}

const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} Ko`
        : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`

export default function LoanPdfExtractProgress({
    fileName,
    fileSize,
    stepIndex,
    elapsedMs,
    progressRatio,
    remainingSeconds,
    isOvertime,
    onReplace,
}: Props) {
    // Toutes les étapes cochées : le compte à rebours n'a plus de sens, le store est en cours d'écriture
    const isComplete = stepIndex >= PDF_EXTRACT_STEPS.length
    const hint = isComplete
        ? PDF_EXTRACT_FILLING
        : isOvertime
          ? elapsedMs >= PDF_EXTRACT_SLOW_HINT_MS
              ? PDF_EXTRACT_SLOW_HINT
              : PDF_EXTRACT_ALMOST_DONE
          : pdfExtractRemainingLabel(remainingSeconds)

    return (
        <div
            data-test="pdf-extract-progress"
            className="rounded-2xl border border-rm-blue bg-rm-blue-accent-bg p-4 md:p-5"
        >
            <div className="flex items-center gap-2 rounded-xl border border-rm-gray-light bg-white px-3 py-2">
                <i className="fa-regular fa-folder text-pale/70" />
                <span className="min-w-0 truncate text-[14px] text-pale">{fileName}</span>
                <span className="shrink-0 text-[13px] text-pale/60">{formatSize(fileSize)}</span>
                <span className="flex-1" />
                <button
                    type="button"
                    onClick={onReplace}
                    data-test="pdf-extract-replace"
                    className="shrink-0 text-[14px] font-semibold text-rm-blue-accent underline"
                >
                    Remplacer
                </button>
            </div>

            <ul role="status" aria-live="polite" className="m-0 mt-4 flex list-none flex-col gap-2 p-0">
                {PDF_EXTRACT_STEPS.map((step, index) => {
                    const isDone = index < stepIndex
                    const isCurrent = index === stepIndex

                    return (
                        <li key={step.label} className="flex items-center gap-2.5">
                            {isDone ? (
                                <i className="fa-regular fa-circle-check text-green-800" />
                            ) : isCurrent ? (
                                <i className="fa-regular fa-spinner fa-spin text-rm-orange" />
                            ) : (
                                <i className="fa-regular fa-circle text-pale/30" />
                            )}
                            <span
                                className={`text-[14px] ${
                                    isDone ? 'text-green-800' : isCurrent ? 'font-semibold text-pale' : 'text-pale/50'
                                }`}
                            >
                                {step.label}
                            </span>
                        </li>
                    )
                })}
            </ul>

            <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progressRatio * 100)}
                data-test="pdf-extract-progressbar"
                className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-rm-blue-light/60"
            >
                <div
                    className="h-full rounded-full bg-rm-blue transition-[width] duration-300"
                    style={{ width: `${Math.round(progressRatio * 100)}%` }}
                />
            </div>

            <p className="m-0 mt-2 text-[13px] text-pale/70">{hint}</p>
        </div>
    )
}
